import React, { useEffect, useState } from 'react';
import { Code, Shield, Zap, Lock, CheckCircle, GitBranch } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const SecureDevelopment = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.5}px)`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 opacity-15 bg-[linear-gradient(rgba(11,112,42,0.25)_1px,transparent_1px),linear-gradient(90deg,rgba(11,112,42,0.25)_1px,transparent_1px)] bg-[size:50px_50px]" style={{ transform: `translateY(${scrollY * 0.25}px)`, willChange: 'transform' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#0C9F39]/40 to-black" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#014421]/80 backdrop-blur-sm rounded-full border border-[#0B702A]/30 mb-8">
            <Code className="w-4 h-4 text-[#0B702A]" />
            <span className="text-sm">{t.secureDev.badge}</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">{t.secureDev.title}</h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t.secureDev.subtitle}
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.secureDev.differential}</h2>
            <p className="text-xl text-gray-400">{t.secureDev.differentialSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <Code className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.secureDev.features.code.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.secureDev.features.code.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.secureDev.features.protection.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.secureDev.features.protection.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.secureDev.features.performance.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.secureDev.features.performance.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.secureDev.features.encryption.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.secureDev.features.encryption.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <GitBranch className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.secureDev.features.cicd.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.secureDev.features.cicd.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.secureDev.features.testing.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.secureDev.features.testing.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies with Parallax */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1619410283995-43d9134e7656?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.2}px) scale(${1 + scrollY * 0.0001})`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#014421]/20 to-black" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.secureDev.technologies}</h2>
            <p className="text-xl text-gray-400">{t.secureDev.technologiesSubtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            <div className="p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:border-[#0B702A]/60 transition-all duration-300">
              <div className="text-sm text-[#0B702A] font-semibold mb-2">{t.secureDev.techStack.frontend}</div>
              <div className="text-white font-medium">React / Next.js</div>
            </div>

            <div className="p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:border-[#0B702A]/60 transition-all duration-300">
              <div className="text-sm text-[#0B702A] font-semibold mb-2">{t.secureDev.techStack.backend}</div>
              <div className="text-white font-medium">Node.js / Python</div>
            </div>

            <div className="p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:border-[#0B702A]/60 transition-all duration-300">
              <div className="text-sm text-[#0B702A] font-semibold mb-2">{t.secureDev.techStack.database}</div>
              <div className="text-white font-medium">PostgreSQL / MongoDB</div>
            </div>

            <div className="p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:border-[#0B702A]/60 transition-all duration-300">
              <div className="text-sm text-[#0B702A] font-semibold mb-2">{t.secureDev.techStack.cloud}</div>
              <div className="text-white font-medium">AWS / Azure / GCP</div>
            </div>

            <div className="p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:border-[#0B702A]/60 transition-all duration-300">
              <div className="text-sm text-[#0B702A] font-semibold mb-2">{t.secureDev.techStack.devops}</div>
              <div className="text-white font-medium">Docker / Kubernetes</div>
            </div>

            <div className="p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl text-center hover:border-[#0B702A]/60 transition-all duration-300">
              <div className="text-sm text-[#0B702A] font-semibold mb-2">{t.secureDev.techStack.auth}</div>
              <div className="text-white font-medium">OAuth / JWT</div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.secureDev.process}</h2>
            <p className="text-xl text-gray-400">{t.secureDev.processSubtitle}</p>
          </div>

          <div className="space-y-8">
            <div className="flex items-center gap-6 p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 transition-all duration-300">
              <div className="flex-shrink-0 w-16 h-16 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-2xl font-bold text-[#0B702A]">1</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">{t.secureDev.processSteps.planning.title}</h3>
                <p className="text-gray-400">{t.secureDev.processSteps.planning.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 transition-all duration-300">
              <div className="flex-shrink-0 w-16 h-16 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-2xl font-bold text-[#0B702A]">2</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">{t.secureDev.processSteps.development.title}</h3>
                <p className="text-gray-400">{t.secureDev.processSteps.development.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 transition-all duration-300">
              <div className="flex-shrink-0 w-16 h-16 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-2xl font-bold text-[#0B702A]">3</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">{t.secureDev.processSteps.testing.title}</h3>
                <p className="text-gray-400">{t.secureDev.processSteps.testing.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-6 p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 transition-all duration-300">
              <div className="flex-shrink-0 w-16 h-16 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-2xl font-bold text-[#0B702A]">4</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">{t.secureDev.processSteps.deploy.title}</h3>
                <p className="text-gray-400">{t.secureDev.processSteps.deploy.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.secureDev.ctaTitle}</h2>
          <p className="text-xl text-gray-400 mb-10">{t.secureDev.ctaSubtitle}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#0B702A] text-white text-lg font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:shadow-[#0B702A]/50 hover:scale-105"
          >
            {t.secureDev.ctaButton}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SecureDevelopment;